import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  CenterIndicatorComponent,
  PhaseIntensityBarComponent,
  RANKING_RSABTS_RSABT_MAX_INTENSITY_DEFAULT_COLUMNS,
  RsabtIndicatorComponent,
  TableColumn,
  TableComponent,
} from '@ui';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BadgeIndicatorComponent } from '../../molecules/badge-indicator/badge-indicator.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MaxIntensityRankingRsabt } from '@data';
import { TimeIndicatorComponent } from '../../molecules/indicators/time-indicator.component';
import { NzEmptyModule } from 'ng-zorro-antd/empty';

@Component({
  selector: 'ui-max-intensity-rsabt-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableComponent,
    TranslocoDirective,
    BadgeIndicatorComponent,
    PhaseIntensityBarComponent,
    CenterIndicatorComponent,
    RsabtIndicatorComponent,
    TimeIndicatorComponent,
    NzEmptyModule,
  ],
  template: `
    <ng-container *transloco="let t">
      @if (dataSource().length && columns()!.length && columnTemplates()) {
        <ui-table
          [dataSource]="dataSource()"
          [columns]="columns()"
          [columnTemplates]="columnTemplates()"
          [showPagination]="showPagination()"
          [loading]="loading()"
        />
      } @else if (!loading()) {
        <nz-empty
          nzNotFoundImage="simple"
          [nzNotFoundContent]="emptyContentTemplate"
          class="py-8"
        ></nz-empty>
      }

      <ng-template #emptyContentTemplate>
        <span style="color: #737373; font-size: 0.875rem;">{{
          t('CHARTS.VOID_TABLE_MESSAGE')
        }}</span>
      </ng-template>

      <ng-template #rsabtTemplate let-data="row">
        <ui-rsabt-indicator
          [showCenterCode]="showCenterCode()"
          [name]="data?.rsabtName"
          [id]="data?.id"
          [distributorR1]="data?.distributorR1"
          [distributorId]="data.distributorId"
          [rsabtId]="data.rsabtId"
          [transformerId]="data.transformerId"
          [centerId]="data.centerId"
          [vtnId]="data.vtnId"
          (onCenter)="emitCenter($event)"
        />
      </ng-template>

      <ng-template #centerTemplate let-data="row">
        <ui-center-indicator
          [indicators]="data?.transformers"
          [centerName]="data?.centerName"
          [centerId]="data.centerId"
          [distributorId]="data.distributorId"
          [rsabtId]="data.rsabtId"
          [vtnId]="data.vtnId"
          (onRsabt)="emitRsabtId($event)"
          (onCenter)="emitCenter($event)"
        />
      </ng-template>

      <ng-template #maxIntensityTemplate let-data="row">
        <ui-badge-indicator
          [value]="data.intensity"
          [percentage]="data.intensityPercentage"
          [nominal]="data.intensityNominal"
          [units]="'A'"
          [tooltipLabel]="
            t('RSABT_MAX_INTENSITY.TOOLTIP_INTENSITY_TEMPLATE_LABEL')
          "
        />
      </ng-template>

      <ng-template #intensityPhaseTemplate let-data="row">
        <ui-phase-intensity-bar
          [lineIntensity]="
            data.intensityPhaseR + data.intensityPhaseS + data.intensityPhaseT
          "
          [intensityPhaseR]="data.intensityPhaseR"
          [intensityPhaseS]="data.intensityPhaseS"
          [intensityPhaseT]="data.intensityPhaseT"
        />
      </ng-template>

      <ng-template #timestampTemplate let-data="row">
        <ui-time-indicator [timestamp]="data.timestamp" />
      </ng-template>
    </ng-container>
  `,
})
export class MaxIntensityRsabtTableComponent {
  DEFAULT_COLUMNS: TableColumn[] =
    RANKING_RSABTS_RSABT_MAX_INTENSITY_DEFAULT_COLUMNS;

  protected readonly translocoService = inject(TranslocoService);
  protected readonly destroyRef = inject(DestroyRef);

  data = input.required<MaxIntensityRankingRsabt[]>();
  loading = input<boolean>(false);
  showPagination = input<boolean>(false);
  showCenterCode = input<boolean>(false);

  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    vtnId: string;
  }>();
  onRsabtId = output<string>();

  columnTemplates = signal<{ [key: string]: any }>({});
  columns = signal<TableColumn[]>(this.DEFAULT_COLUMNS);

  dataSource = computed(() =>
    this.data().map((item) => {
      // try to use an explicit id if backend provides it
      const stableId =
        (item as any).id ??
        // fallback to a combination of stable fields (adjust fields to your model)
        `${item.centerId ?? ''}_${new Date(item.timestamp ?? '').getTime() || 0}_${item.rsabtId ?? ''}_${item.transformerId ?? ''}`;
      return { ...item, id: stableId };
    }),
  );

  rsabtTemplate = viewChild('rsabtTemplate');
  centerTemplate = viewChild('centerTemplate');
  maxIntensityTemplate = viewChild('maxIntensityTemplate');
  intensityPhaseTemplate = viewChild('intensityPhaseTemplate');
  timestampTemplate = viewChild('timestampTemplate');

  columnTemplatesSet = false;

  e1 = effect(() => {
    if (
      this.rsabtTemplate() &&
      this.centerTemplate() &&
      this.maxIntensityTemplate() &&
      this.intensityPhaseTemplate() &&
      this.timestampTemplate() &&
      !this.columnTemplatesSet
    ) {
      this.columnTemplatesSet = true;
      this.setColumnTemplates();
    }
  });

  constructor() {
    this.setLocalizeColumns();
  }

  setColumnTemplates() {
    this.columnTemplates.set({
      rsabt: this.rsabtTemplate(),
      center: this.centerTemplate(),
      maxIntensity: this.maxIntensityTemplate(),
      intensityPhase: this.intensityPhaseTemplate(),
      timestamp: this.timestampTemplate(),
    });
  }

  setLocalizeColumns() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns.set(
          this.DEFAULT_COLUMNS.map((col) => ({
            ...col,
            title: this.translocoService.translate(col.title),
          })),
        );
      });
  }

  emitRsabtId(id: string) {
    this.onRsabtId.emit(id);
  }
  emitCenter(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
    vtnId: string;
  }) {
    this.onCenter.emit(event);
  }
}
